import type { BrowserSession } from '../gateway/contracts';

const STORAGE_KEY = 'nord.browserSession.v1';
const PET_FOLLOWING_SEQUENCE_KEY = 'nord.petFollowingSequence.v1';
const PET_TREAT_SEQUENCE_KEY = 'nord.petTreatSequence.v1';
const TREASURE_PICKUP_SEQUENCE_KEY = 'nord.treasurePickupSequence.v1';
const BUILDING_PURCHASE_SEQUENCE_KEY = 'nord.buildingPurchaseSequence.v1';
const BUILDING_EDIT_SEQUENCE_KEY = 'nord.buildingEditSequence.v1';
const BUILDING_PRODUCTION_SEQUENCE_KEY = 'nord.buildingProductionSequence.v1';
const INVENTORY_MOVE_SEQUENCE_KEY = 'nord.inventoryMoveSequence.v1';
const MOUNT_TRANSITION_SEQUENCE_KEY = 'nord.mountTransitionSequence.v1';
const MOUNT_PURCHASE_SEQUENCE_KEY = 'nord.mountPurchaseSequence.v1';
const PET_PURCHASE_SEQUENCE_KEY = 'nord.petPurchaseSequence.v1';
const THEME_PURCHASE_SEQUENCE_KEY = 'nord.themePurchaseSequence.v1';
const INVENTORY_ACTIVATION_SEQUENCE_KEY = 'nord.inventoryActivationSequence.v1';
const WEATHER_ACTIVATION_SEQUENCE_KEY = 'nord.weatherActivationSequence.v1';
const RESOURCE_SLOT_MOVE_SEQUENCE_KEY = 'nord.resourceSlotMoveSequence.v1';
const TOMBOLA_SEQUENCE_KEY = 'nord.tombolaSequence.v1';
const PASSWORD_CHANGE_SEQUENCE_KEY = 'nord.passwordChangeSequence.v1';
const VILLAGE_TRAVEL_SEQUENCE_KEY = 'nord.villageTravelSequence.v1';

export class SessionStore {
  private phoneNoticeToken: string | undefined;
  private liveSession: BrowserSession | undefined;

  phoneNoticeWasShown(token: string): boolean {
    return this.phoneNoticeToken === token;
  }

  markPhoneNoticeShown(token: string): void {
    this.phoneNoticeToken = token;
    if (this.liveSession?.token === token) this.write(this.liveSession);
  }

  read(): BrowserSession | undefined {
    try {
      const serialized = sessionStorage.getItem(STORAGE_KEY);
      if (serialized === null) return undefined;
      const candidate: unknown = JSON.parse(serialized);
      if (!isStoredSession(candidate)) {
        this.clear();
        return undefined;
      }
      const restored = { ...candidate } as BrowserSession & { phoneNoticeShown?: unknown };
      this.phoneNoticeToken = restored.phoneNoticeShown === true ? restored.token : undefined;
      delete restored.phoneNoticeShown;
      this.liveSession = restored;
      return restored;
    } catch {
      this.clear();
      return undefined;
    }
  }

  write(session: BrowserSession): void {
    this.liveSession = session;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...session,
          phoneNoticeShown: this.phoneNoticeWasShown(session.token),
        }),
      );
    } catch {
      // The live in-memory session remains usable if browser storage is unavailable.
    }
  }

  readPetFollowingSequence(token: string): number {
    try {
      const serialized = sessionStorage.getItem(PET_FOLLOWING_SEQUENCE_KEY);
      if (serialized === null) return 0;
      const candidate: unknown = JSON.parse(serialized);
      if (typeof candidate !== 'object' || candidate === null) return 0;
      const stored = candidate as Readonly<{ token?: unknown; sequence?: unknown }>;
      return stored.token === token &&
        typeof stored.sequence === 'number' &&
        Number.isSafeInteger(stored.sequence) &&
        stored.sequence >= 0
        ? stored.sequence
        : 0;
    } catch {
      return 0;
    }
  }

  writePetFollowingSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Pet following sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(PET_FOLLOWING_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readPetTreatSequence(token: string): number {
    return this.readSequence(PET_TREAT_SEQUENCE_KEY, token);
  }

  writePetTreatSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Pet treat sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(PET_TREAT_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readTreasurePickupSequence(token: string): number {
    return this.readSequence(TREASURE_PICKUP_SEQUENCE_KEY, token);
  }

  writeTreasurePickupSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Treasure pickup sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(TREASURE_PICKUP_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readBuildingPurchaseSequence(token: string): number {
    return this.readSequence(BUILDING_PURCHASE_SEQUENCE_KEY, token);
  }

  writeBuildingPurchaseSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Building purchase sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(BUILDING_PURCHASE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readBuildingEditSequence(token: string): number {
    return this.readSequence(BUILDING_EDIT_SEQUENCE_KEY, token);
  }

  writeBuildingEditSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Building edit sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(BUILDING_EDIT_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readBuildingProductionSequence(token: string): number {
    return this.readSequence(BUILDING_PRODUCTION_SEQUENCE_KEY, token);
  }

  writeBuildingProductionSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Building production sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(BUILDING_PRODUCTION_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readInventoryMoveSequence(token: string): number {
    return this.readSequence(INVENTORY_MOVE_SEQUENCE_KEY, token);
  }

  readMountTransitionSequence(token: string): number {
    return this.readSequence(MOUNT_TRANSITION_SEQUENCE_KEY, token);
  }

  writeMountTransitionSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Mount transition sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(MOUNT_TRANSITION_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readMountPurchaseSequence(token: string): number {
    return this.readSequence(MOUNT_PURCHASE_SEQUENCE_KEY, token);
  }

  writeMountPurchaseSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Mount purchase sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(MOUNT_PURCHASE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readPetPurchaseSequence(token: string): number {
    return this.readSequence(PET_PURCHASE_SEQUENCE_KEY, token);
  }

  writePetPurchaseSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Pet purchase sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(PET_PURCHASE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readThemePurchaseSequence(token: string): number {
    return this.readSequence(THEME_PURCHASE_SEQUENCE_KEY, token);
  }

  writeThemePurchaseSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Theme purchase sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(THEME_PURCHASE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  writeInventoryMoveSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Inventory move sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(INVENTORY_MOVE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readInventoryActivationSequence(token: string): number {
    return this.readSequence(INVENTORY_ACTIVATION_SEQUENCE_KEY, token);
  }

  writeInventoryActivationSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Inventory activation sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(
        INVENTORY_ACTIVATION_SEQUENCE_KEY,
        JSON.stringify({ token, sequence }),
      );
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readWeatherActivationSequence(token: string): number {
    return this.readSequence(WEATHER_ACTIVATION_SEQUENCE_KEY, token);
  }

  writeWeatherActivationSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Weather activation sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(WEATHER_ACTIVATION_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readResourceSlotMoveSequence(token: string): number {
    return this.readSequence(RESOURCE_SLOT_MOVE_SEQUENCE_KEY, token);
  }

  writeResourceSlotMoveSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Resource-slot move sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(RESOURCE_SLOT_MOVE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readTombolaSequence(token: string): number {
    return this.readSequence(TOMBOLA_SEQUENCE_KEY, token);
  }

  writeTombolaSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Tombola sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(TOMBOLA_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readPasswordChangeSequence(token: string): number {
    return this.readSequence(PASSWORD_CHANGE_SEQUENCE_KEY, token);
  }

  writePasswordChangeSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Password-change sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(PASSWORD_CHANGE_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  readVillageTravelSequence(token: string): number {
    return this.readSequence(VILLAGE_TRAVEL_SEQUENCE_KEY, token);
  }

  writeVillageTravelSequence(token: string, sequence: number): void {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Village-travel sequence must be a non-negative safe integer');
    }
    try {
      sessionStorage.setItem(VILLAGE_TRAVEL_SEQUENCE_KEY, JSON.stringify({ token, sequence }));
    } catch {
      // The in-memory sequence still protects this page while the session remains active.
    }
  }

  private readSequence(key: string, token: string): number {
    try {
      const serialized = sessionStorage.getItem(key);
      if (serialized === null) return 0;
      const candidate: unknown = JSON.parse(serialized);
      if (typeof candidate !== 'object' || candidate === null) return 0;
      const stored = candidate as Readonly<{ token?: unknown; sequence?: unknown }>;
      return stored.token === token &&
        typeof stored.sequence === 'number' &&
        Number.isSafeInteger(stored.sequence) &&
        stored.sequence >= 0
        ? stored.sequence
        : 0;
    } catch {
      return 0;
    }
  }

  clear(): void {
    this.phoneNoticeToken = undefined;
    this.liveSession = undefined;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(PET_FOLLOWING_SEQUENCE_KEY);
      sessionStorage.removeItem(PET_TREAT_SEQUENCE_KEY);
      sessionStorage.removeItem(TREASURE_PICKUP_SEQUENCE_KEY);
      sessionStorage.removeItem(BUILDING_PURCHASE_SEQUENCE_KEY);
      sessionStorage.removeItem(BUILDING_EDIT_SEQUENCE_KEY);
      sessionStorage.removeItem(BUILDING_PRODUCTION_SEQUENCE_KEY);
      sessionStorage.removeItem(INVENTORY_MOVE_SEQUENCE_KEY);
      sessionStorage.removeItem(MOUNT_TRANSITION_SEQUENCE_KEY);
      sessionStorage.removeItem(MOUNT_PURCHASE_SEQUENCE_KEY);
      sessionStorage.removeItem(PET_PURCHASE_SEQUENCE_KEY);
      sessionStorage.removeItem(THEME_PURCHASE_SEQUENCE_KEY);
      sessionStorage.removeItem(INVENTORY_ACTIVATION_SEQUENCE_KEY);
      sessionStorage.removeItem(WEATHER_ACTIVATION_SEQUENCE_KEY);
      sessionStorage.removeItem(RESOURCE_SLOT_MOVE_SEQUENCE_KEY);
      sessionStorage.removeItem(TOMBOLA_SEQUENCE_KEY);
      sessionStorage.removeItem(PASSWORD_CHANGE_SEQUENCE_KEY);
      sessionStorage.removeItem(VILLAGE_TRAVEL_SEQUENCE_KEY);
    } catch {
      // Nothing else can be done in a storage-restricted context.
    }
  }
}

function isStoredSession(value: unknown): value is BrowserSession {
  if (typeof value !== 'object' || value === null) return false;
  const session = value as Partial<BrowserSession>;
  return (
    typeof session.token === 'string' &&
    typeof session.playerId === 'number' &&
    typeof session.level === 'number' &&
    typeof session.spins === 'number' &&
    typeof session.vip === 'boolean' &&
    Array.isArray(session.villages) &&
    typeof session.createdAt === 'string'
  );
}
